import PropTypes from 'prop-types';
import React, { createContext, useCallback, useMemo, useReducer, useRef } from 'react';

import { useGlobalKeyDown } from '../../hooks/use-global-key-down';
import { LOW_PRIORITY_QUEUE } from '../global-event-listener/constants';
import { addHotkey as addHotkeyFn, removeHotkey as removeHotkeyFn, setHotkeys as setHotkeysFn } from './actions';
import { convertEventToKeyObject } from '../../services/hotkey-service/key-transforms';
import { convertInputBufferToString } from '../../services/hotkey-service/convert-input-buffer-to-string';
import { hotkeyService } from '../../services/hotkey-service';
import { logger } from '../../services/hotkey-service/logger';
import { reducer, INITIAL_STATE } from './reducer';
import { shouldAddEventKey } from '../../services/hotkey-service/should-add-event-key';

const TAB_PREVENTION_TIMEOUT_IN_MS = 50;

export const Context = createContext();
const { Consumer, Provider } = Context;

/**
 * @param {{ children: import('react').ReactNode, eventTargetRef?: { current: EventTarget | null } | null }} props
 */
const ProviderWrapper = ({ children, eventTargetRef = null }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { hotkeys } = state;
  const inputBuffer = useRef([]);
  const preventTabs = useRef(false);
  const tabTimeout = useRef(null);

  const addHotkey = useCallback((...args) => {
    dispatch(addHotkeyFn(...args));
  }, []);

  const removeHotkey = useCallback((...args) => {
    dispatch(removeHotkeyFn(...args));
  }, []);

  const setHotkeys = useCallback((...args) => {
    dispatch(setHotkeysFn(...args));
  }, []);

  const getCurrentHotkeyText = useCallback(() => {
    return convertInputBufferToString(inputBuffer.current);
  }, []);

  const onHotkeyMatch = useCallback(() => {
    preventTabs.current = true;
    logger.log(`Turning off tabs for ${TAB_PREVENTION_TIMEOUT_IN_MS}ms`);

    if (tabTimeout.current) {
      clearTimeout(tabTimeout.current);
      tabTimeout.current = null;
    }

    tabTimeout.current = setTimeout(() => {
      preventTabs.current = false;
      tabTimeout.current = null;
    }, TAB_PREVENTION_TIMEOUT_IN_MS);
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (preventTabs.current && event.key === 'Tab') {
        logger.log('Prevented Tab');
        event.preventDefault();
      }

      if (!shouldAddEventKey(event)) {
        return;
      }

      inputBuffer.current.push(convertEventToKeyObject(event));
      hotkeyService.execute(inputBuffer.current, hotkeys, onHotkeyMatch);
    },
    [hotkeys, onHotkeyMatch],
  );

  useGlobalKeyDown(onKeyDown, { queue: LOW_PRIORITY_QUEUE, targetRef: eventTargetRef });

  const value = useMemo(
    () => ({
      addHotkey,
      getCurrentHotkeyText,
      hotkeys,
      removeHotkey,
      setHotkeys,
    }),
    [addHotkey, getCurrentHotkeyText, hotkeys, removeHotkey, setHotkeys],
  );
  return <Provider value={value}>{children}</Provider>;
};

ProviderWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  eventTargetRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export { Consumer, ProviderWrapper as Provider };
